/**
 * Recommended actions, each with the reasoning that produced it.
 *
 * Some carry a command that sets the dashboard up to act on them. That button
 * is the only place in a reply where the user changes the screen deliberately
 * rather than as a side effect of asking, so it says what it will do and does
 * exactly that — one command, on click, nothing on render.
 *
 * A card with a command ends in a button carrying the agent's own label for
 * it ("Switch to Non-College Under $100k"), word for word. A recommendation
 * with no command is advice and has no button. Every card looks the same: the
 * agent's priority is not reliable enough to dress one up as more important.
 */

import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import {
  FiArrowUpRight,
  FiCalendar,
  FiCompass,
  FiGlobe,
  FiLayout,
  FiSliders,
  FiTag,
  FiUsers,
} from "react-icons/fi";
import { handleLouCommand } from "../../louCommandBridge/commandRouter";
import InlineText from "./InlineText";

/**
 * `selection.set_period` appears in the block specification but is not a
 * command: the contract has `set_time_period`, and names its dates `start`
 * and `end`. Translated here rather than refused, because the intent is
 * unambiguous and the alternative is a dead button.
 */
const translate = ({ command, params = {} }) => {
  if (command !== "selection.set_period") return { command, params };
  const { start_date: start, end_date: end, ...rest } = params;
  return {
    command: "selection.set_time_period",
    params: { preset: "CUSTOM", ...(start ? { start } : {}), ...(end ? { end } : {}), ...rest },
  };
};

/** An icon for the part of the dashboard each command changes. */
const ICONS = {
  "selection.set_segments": FiUsers,
  "selection.set_brands": FiTag,
  "selection.set_module": FiLayout,
  "selection.set_markets": FiGlobe,
  "selection.set_period": FiCalendar,
  "selection.set_time_period": FiCalendar,
};

const RecommendationDeck = ({ recommendations = [] }) => {
  const dispatch = useDispatch();
  if (!recommendations.length) return null;

  // A pressed button looks the same as before: the dashboard changing is the
  // feedback, and a tick here would claim success the panel cannot confirm.
  const run = (action) => {
    const { command, params } = translate(action);
    dispatch(
      handleLouCommand({
        command,
        params,
        metadata: { version: "1.8", command_id: `rec_${Date.now()}` },
      }),
    );
  };

  return (
    <section className="lou-recs" aria-label="Recommended actions">
      <div className="lou-recs__list">
        {recommendations.map((rec, i) => {
          const action = rec.actionCommand?.command ? rec.actionCommand : null;
          const Icon = action ? ICONS[action.command] ?? FiSliders : FiCompass;

          return (
            <article className="lou-recs__card" key={`${rec.title}-${i}`}>
              <div className="lou-recs__main">
                <span className="lou-recs__icon" aria-hidden="true">
                  <Icon size={16} />
                </span>
                <div className="lou-recs__body">
                  <h5 className="lou-recs__title"><InlineText text={rec.title} /></h5>
                  {rec.rationale ? (
                    <p className="lou-recs__rationale"><InlineText text={rec.rationale} /></p>
                  ) : null}
                </div>
              </div>

              {/* Only an action that changes the dashboard gets a button; it
                  carries the agent's label exactly as written. */}
              {action ? (
                <button
                  type="button"
                  className="lou-recs__action"
                  onClick={() => run(action)}
                >
                  <InlineText text={action.label} />
                  <FiArrowUpRight size={14} aria-hidden="true" />
                </button>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
};

RecommendationDeck.propTypes = { recommendations: PropTypes.array };

export default RecommendationDeck;
